import {Theme} from '../theme/interface';
import {useTheme} from '../theme';
import {ImageStyle, Platform, PlatformOSType, StyleSheet, TextStyle, ViewStyle} from 'react-native';
import {DependencyList, useMemo} from 'react';
import {DeviceSize, useDeviceSize, AlignFunc, useAlign} from '../theme/helpers';
import {useIsDesktop} from './useIsDesktop';
// StylesDictionary no longer exported from react-native-render-html
type StylesDictionary = Record<string, any>;
import {useDimensions} from './useDimensions';

export type NamedStyles<T> = StyleSheet.NamedStyles<T>;
export type Styles = ViewStyle | TextStyle | ImageStyle;

interface ShadowCreatorParams {
  width?: number;
  height?: number;
  radius: number;
  opacity?: number;
  color?: string;
  elevation?: number; // for android https://reactnative.dev/docs/shadow-props#reference
}

export type ShadowCreator = (params: ShadowCreatorParams) => Styles;
export interface StyleHelper {
  align: AlignFunc;
  deviceSize: DeviceSize;
  deviceWidth: number;
  deviceHeight: number;
  theme: Theme;
  isDesktop: boolean;
  mediaOnly(deviceSize: DeviceSize, styles: Styles, otherwiseStyles?: Styles): Styles;
  mediaExclude(deviceSize: DeviceSize, styles: Styles, otherwiseStyles?: Styles): Styles;
  platformOnly(platform: PlatformOSType, styles: Styles, otherwiseStyles?: Styles): Styles;
  addShadow: ShadowCreator;
}

export const createStyleHelper = (
  theme: Theme,
  isDesktop: boolean,
  deviceSize: DeviceSize,
  align: AlignFunc,
  deviceWidth: number,
  deviceHeight: number,
): StyleHelper => {
  return {
    align,
    deviceSize,
    deviceWidth,
    deviceHeight,
    theme,
    isDesktop,
    addShadow({
      radius,
      width = 0,
      height = 0,
      opacity = 0.1,
      color = theme.color.primaryBlackColor,
      elevation = 5,
    }: ShadowCreatorParams): Styles {
      return {
        shadowColor: color,
        shadowRadius: radius,
        shadowOpacity: opacity,
        shadowOffset: {width, height},
        elevation,
      };
    },
    mediaOnly(ds: DeviceSize, styles: Styles, otherwiseStyles: Styles = {}): Styles {
      return ds === deviceSize ? styles : otherwiseStyles;
    },
    mediaExclude(ds: DeviceSize, styles: Styles, otherwiseStyles: Styles = {}): Styles {
      return ds !== deviceSize ? styles : otherwiseStyles;
    },
    platformOnly(platform: PlatformOSType, styles: Styles, otherwiseStyles: Styles = {}): Styles {
      return Platform.OS === platform ? styles : otherwiseStyles;
    },
  };
};

export type HookWrappedStyles<T extends NamedStyles<T> | NamedStyles<any>> = (
  helper: StyleHelper,
) => T | NamedStyles<T>;

export const useStyles = <T extends NamedStyles<T> | NamedStyles<any>>(
  styleCreator: HookWrappedStyles<T>,
  deps?: DependencyList,
): T => {
  const theme = useTheme();
  const deviceSize = useDeviceSize();
  const align = useAlign();
  const isDesktop = useIsDesktop();
  const {width: deviceWidth, height: deviceHeight} = useDimensions();

  return useMemo(
    () =>
      StyleSheet.create(
        styleCreator(createStyleHelper(theme, isDesktop, deviceSize, align, deviceWidth, deviceHeight)),
      ) as T,
    [theme, deviceSize, ...(deps ? deps : [])],
  );
};

export const useStylesHtml = <T = StylesDictionary>(
  styleCreator: (helper: StyleHelper) => T,
  deps?: DependencyList,
): T => {
  const theme = useTheme();
  const deviceSize = useDeviceSize();
  const align = useAlign();
  const isDesktop = useIsDesktop();
  const {width: deviceWidth, height: deviceHeight} = useDimensions();

  return useMemo(
    () => styleCreator(createStyleHelper(theme, isDesktop, deviceSize, align, deviceWidth, deviceHeight)),
    [theme, deviceSize, ...(deps ? deps : [])],
  );
};
